import React, { useState, useRef, useCallback } from "react";
import { format, startOfDay } from "date-fns";
import "./FormattedDateInput.css";

interface FormattedDateInputProps {
  value: Date;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  preventFuture?: boolean;
}

const MAX_DAYS: Record<number, number> = {
  1: 31, 2: 29, 3: 31, 4: 30, 5: 31, 6: 30,
  7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31,
};

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const formatDisplay = (date: Date): string => format(date, "MMM dd, yyyy");

// Fixed-position slot model: 8 slots for MM/DD/YYYY
// Each slot is a digit character or null (empty)
const dateToSlots = (d: Date): (string | null)[] =>
  format(d, "MMddyyyy").split("");

const slotsToFormatted = (slots: (string | null)[]): string => {
  const c = (i: number) => slots[i] || "_";
  return `${c(0)}${c(1)}/${c(2)}${c(3)}/${c(4)}${c(5)}${c(6)}${c(7)}`;
};

const allSlotsFilled = (slots: (string | null)[]): boolean =>
  slots.length === 8 && slots.every((s) => s !== null);

// Formatted string: M M / D D / Y Y Y Y
// Char position:    0 1 2 3 4 5 6 7 8 9
// Slot index:       0 1 - 2 3 - 4 5 6 7
const CHAR_TO_SLOT: Record<number, number> = { 0: 0, 1: 1, 3: 2, 4: 3, 6: 4, 7: 5, 8: 6, 9: 7 };
const SLOT_TO_CHAR = [0, 1, 3, 4, 6, 7, 8, 9];

// Find the slot at or after cursor position (for typing/delete)
const slotAtCursor = (pos: number): number => {
  for (let p = pos; p <= 9; p++) {
    if (CHAR_TO_SLOT[p] !== undefined) return CHAR_TO_SLOT[p];
  }
  return 8; // past end
};

// Find the slot before cursor position (for backspace)
const slotBeforeCursor = (pos: number): number => {
  for (let p = pos - 1; p >= 0; p--) {
    if (CHAR_TO_SLOT[p] !== undefined) return CHAR_TO_SLOT[p];
  }
  return -1;
};

// Get all slot indices covered by a selection range [start, end)
const slotsInSelection = (start: number, end: number): number[] => {
  const result: number[] = [];
  for (let p = start; p < end; p++) {
    if (CHAR_TO_SLOT[p] !== undefined) result.push(CHAR_TO_SLOT[p]);
  }
  return result;
};

const FormattedDateInput: React.FC<FormattedDateInputProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  preventFuture,
}) => {
  const [displayValue, setDisplayValue] = useState(formatDisplay(value));
  const [error, setError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const slotsRef = useRef<(string | null)[]>(dateToSlots(value));
  const isFocusedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync prop changes when not focused
  const lastPropRef = useRef(value);
  if (value !== lastPropRef.current && !isFocusedRef.current) {
    lastPropRef.current = value;
    setDisplayValue(formatDisplay(value));
    slotsRef.current = dateToSlots(value);
    if (error) setError(null);
  }

  const validate = useCallback(
    (slots: (string | null)[]): string | null => {
      if (!allSlotsFilled(slots)) return null;

      const month = parseInt(`${slots[0]}${slots[1]}`, 10);
      const day = parseInt(`${slots[2]}${slots[3]}`, 10);
      const year = parseInt(`${slots[4]}${slots[5]}${slots[6]}${slots[7]}`, 10);

      if (month < 1 || month > 12) return "Month must be between 01 and 12";

      const maxDay = MAX_DAYS[month] || 31;
      if (day < 1 || day > maxDay)
        return `Day must be between 01 and ${maxDay} for month ${slots[0]}${slots[1]}`;

      if (month === 2 && day === 29 && !isLeapYear(year))
        return `Invalid date: ${slotsToFormatted(slots)} does not exist`;

      const parsed = new Date(year, month - 1, day);
      const parsedDay = startOfDay(parsed);

      if (minDate && parsedDay < startOfDay(minDate))
        return `Date cannot be before ${formatDisplay(minDate)}`;
      if (maxDate && parsedDay > startOfDay(maxDate))
        return `Date cannot be after ${formatDisplay(maxDate)}`;
      if (preventFuture && parsedDay > startOfDay(new Date()))
        return "Date cannot be in the future";

      return null;
    },
    [minDate, maxDate, preventFuture]
  );

  const updateSlots = useCallback(
    (newSlots: (string | null)[]) => {
      slotsRef.current = newSlots;
      setDisplayValue(slotsToFormatted(newSlots));

      if (allSlotsFilled(newSlots)) {
        const validationError = validate(newSlots);
        if (validationError) {
          setError(validationError);
        } else {
          setError(null);
          const month = parseInt(`${newSlots[0]}${newSlots[1]}`, 10);
          const day = parseInt(`${newSlots[2]}${newSlots[3]}`, 10);
          const year = parseInt(`${newSlots[4]}${newSlots[5]}${newSlots[6]}${newSlots[7]}`, 10);
          onChange(new Date(year, month - 1, day));
        }
      } else {
        setError(null);
      }
    },
    [validate, onChange]
  );

  const setCursor = (charPos: number) => {
    requestAnimationFrame(() => {
      inputRef.current?.setSelectionRange(charPos, charPos);
    });
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
    setIsFocused(true);
    const slots = dateToSlots(value);
    slotsRef.current = slots;
    setDisplayValue(slotsToFormatted(slots));
    setError(null);
    requestAnimationFrame(() => inputRef.current?.select());
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    setIsFocused(false);
    setError(null);
    setDisplayValue(formatDisplay(value));
    slotsRef.current = dateToSlots(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = inputRef.current;
    if (!input) return;

    const { selectionStart, selectionEnd } = input;
    if (selectionStart === null || selectionEnd === null) return;

    const slots = [...slotsRef.current];
    const hasSelection = selectionStart !== selectionEnd;

    if (e.key >= "0" && e.key <= "9") {
      e.preventDefault();

      if (hasSelection) {
        const selectedSlots = slotsInSelection(selectionStart, selectionEnd);
        if (selectedSlots.length === 0) return;
        selectedSlots.forEach((s) => (slots[s] = null));
        slots[selectedSlots[0]] = e.key;
        updateSlots(slots);
        const nextSlot = selectedSlots[0] + 1;
        setCursor(nextSlot <= 7 ? SLOT_TO_CHAR[nextSlot] : 10);
      } else {
        const slot = slotAtCursor(selectionStart);
        if (slot > 7) return;
        slots[slot] = e.key;
        updateSlots(slots);
        const nextSlot = slot + 1;
        setCursor(nextSlot <= 7 ? SLOT_TO_CHAR[nextSlot] : 10);
      }
    } else if (e.key === "Backspace") {
      e.preventDefault();

      if (hasSelection) {
        const selectedSlots = slotsInSelection(selectionStart, selectionEnd);
        if (selectedSlots.length === 0) return;
        selectedSlots.forEach((s) => (slots[s] = null));
        updateSlots(slots);
        setCursor(SLOT_TO_CHAR[selectedSlots[0]]);
      } else {
        const slot = slotBeforeCursor(selectionStart);
        if (slot < 0) return;
        slots[slot] = null;
        updateSlots(slots);
        setCursor(SLOT_TO_CHAR[slot]);
      }
    } else if (e.key === "Delete") {
      e.preventDefault();

      if (hasSelection) {
        const selectedSlots = slotsInSelection(selectionStart, selectionEnd);
        if (selectedSlots.length === 0) return;
        selectedSlots.forEach((s) => (slots[s] = null));
        updateSlots(slots);
        setCursor(SLOT_TO_CHAR[selectedSlots[0]]);
      } else {
        const slot = slotAtCursor(selectionStart);
        if (slot > 7) return;
        slots[slot] = null;
        updateSlots(slots);
        setCursor(SLOT_TO_CHAR[slot]);
      }
    }
    // Tab, Enter, Escape, Ctrl+A/C, arrows, etc. pass through naturally
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  // Prevent any unhandled input (e.g. IME, drag-drop)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setDisplayValue(slotsToFormatted(slotsRef.current));
  };

  return (
    <div className="fdiContainer">
      <input
        ref={inputRef}
        className={`fdiInput${error ? " fdiInputError" : ""}`}
        type="text"
        inputMode="numeric"
        maxLength={isFocused ? 10 : undefined}
        placeholder={isFocused ? "MM/DD/YYYY" : ""}
        value={displayValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onChange={handleChange}
      />
      {error && <span className="fdiErrorText">{error}</span>}
    </div>
  );
};

export default FormattedDateInput;
